import {
  ExternalTransferTypes,
  type WorkflowCompletion,
} from '@generated/graphql';
import { TextField } from '@ovComponents/2-component/textField/textField';
import { SubAccountSelect } from '@ovComponents/3-pattern/subaccountSelect/subAccountSelect';
import type { FieldRequiredType } from '@ovComponents/4-module/configurableOptionFields/basicField';
import { isNearFullWithdrawal } from '@ovComponents/4-module/workflowCompletion/subSteps/utils/depositAmountRules';
import { withdrawSubAccountFilter } from '@ovComponents/4-module/workflowCompletion/subSteps/withdrawForm/withdrawSubAccountFilter';
import type { TransferContextData } from '@ovComponents/4-module/workflowCompletion/workflowTypes';
import { PageObjectType } from '@pages/setting/PageConfigurationDetail/pageConfigurationTypes';
import { Typography } from 'hephaestus/web';
import { useTranslation } from 'react-i18next';
import { useLocalization } from '../../../../../../hooks/useLocalization';
import { DepositAmount } from '../DepositAmount';
import { showsRequiredFieldError } from '../depositFieldErrors';
import { DepositExternalTransferAccountType } from './DepositExternalTransferAccountType';
import { DepositExternalTransferInstitution } from './DepositExternalTransferInstitution';
import { DepositExternalTransferParticipantNumber } from './DepositExternalTransferParticipantNumber';
import { DepositExternalTransferTypeSelect } from './DepositExternalTransferTypeSelect';
import type { useDepositExternalTransfer } from './useDepositExternalTransfer';

type TransferFieldOption = {
  enabled?: boolean;
  required?: FieldRequiredType;
  label?: unknown;
};

export type DepositExternalTransferProp = {
  deposit: TransferContextData;
  options: {
    subAccount?: TransferFieldOption;
    institution?: TransferFieldOption;
    accountNumber?: TransferFieldOption;
    accountType?: TransferFieldOption;
    transferType?: TransferFieldOption;
    amountCents?: TransferFieldOption;
    displayCustodianAccountNumber?: boolean;
    selectFirstSubAccountOnly?: boolean;
  };
  focused: string[];
  onFocus: (field: string) => void;
  onChange: (value: Partial<TransferContextData>) => void;
  amountError: { hasError: boolean; errorMessage: string };
  userId?: string;
  workflowCompletion?: WorkflowCompletion;
  transfer: ReturnType<typeof useDepositExternalTransfer>;
};

export const DepositExternalTransfer = ({
  deposit,
  options,
  focused,
  onFocus,
  onChange,
  amountError,
  userId,
  workflowCompletion,
  transfer,
}: DepositExternalTransferProp) => {
  const { translateBackendString } = useLocalization();
  const { t } = useTranslation('workflowCompletions');
  const {
    usTransferFlowEnabled,
    enableInKind,
    enabledManualInstitutionCreation,
    participantNumberEnabled,
    originAccountType,
  } = transfer;

  const showToSubAccountSelect =
    options?.subAccount?.enabled &&
    deposit.objectType !== PageObjectType.SUB_ACCOUNT;
  const usOriginOfAccount =
    originAccountType && usTransferFlowEnabled ? originAccountType : undefined;
  const subAccountFilter = withdrawSubAccountFilter({
    contextObjectType: deposit?.objectType,
    contextObjectId: deposit?.objectId,
    completionObjectType: workflowCompletion?.objectType,
    completionObjectId: workflowCompletion?.objectId,
    userId,
  });

  return (
    <>
      {showToSubAccountSelect && (
        <SubAccountSelect
          omitAny
          filter={subAccountFilter}
          sx={{ mb: 2 }}
          dataTestId='deposit-form-sub-account-select'
          label={translateBackendString(options?.subAccount?.label)}
          onSubAccountSelect={(e: any) =>
            onChange({ subAccount: e, accountType: undefined })
          }
          selectedSubAccount={deposit?.subAccount}
          onBlur={() => onFocus('subAccount')}
          error={showsRequiredFieldError({
            missing:
              !deposit?.subAccount ||
              Object.keys(deposit?.subAccount).length === 0,
            field: 'subAccount',
            focused,
            requirement: options?.subAccount?.required,
          })}
          displayCustodianAccountNumber={options.displayCustodianAccountNumber}
          selectFirstSubAccountOnly={options.selectFirstSubAccountOnly}
        />
      )}
      {options?.institution?.enabled && (
        <DepositExternalTransferInstitution
          institution={deposit?.institution}
          option={options.institution}
          focused={focused}
          onFocus={onFocus}
          onChange={onChange}
          addInstitutionManually={enabledManualInstitutionCreation}
          usOriginOfAccount={usOriginOfAccount}
        />
      )}
      {participantNumberEnabled && (
        <DepositExternalTransferParticipantNumber
          participantNumber={deposit?.participantNumber}
          focused={focused}
          onFocus={onFocus}
          onChange={onChange}
        />
      )}
      {options?.accountNumber?.enabled && (
        <TextField
          fullWidth
          sx={{ mb: 2 }}
          testId='deposit-form-account-number'
          label={translateBackendString(options?.accountNumber?.label)}
          onChange={(e: any) => onChange({ accountNumber: e.target.value })}
          value={deposit?.accountNumber}
          onBlur={() => onFocus('accountNumber')}
          error={showsRequiredFieldError({
            missing: !deposit?.accountNumber,
            field: 'accountNumber',
            focused,
            requirement: options?.accountNumber?.required,
          })}
        />
      )}
      {options?.accountType?.enabled &&
        (!usTransferFlowEnabled || originAccountType) && (
          <DepositExternalTransferAccountType
            accountType={deposit?.accountType}
            option={options.accountType}
            focused={focused}
            onFocus={onFocus}
            onChange={onChange}
            usOriginOfAccount={usOriginOfAccount}
          />
        )}
      {options?.transferType?.enabled && (
        <DepositExternalTransferTypeSelect
          transferType={deposit?.transferType}
          option={options.transferType}
          focused={focused}
          onFocus={onFocus}
          onChange={onChange}
          usTransferFlowEnabled={usTransferFlowEnabled}
          enableInKind={enableInKind}
        />
      )}
      {options?.amountCents?.enabled && (
        <DepositAmount
          amountCents={deposit?.amountCents}
          label={
            deposit?.transferType === ExternalTransferTypes.AllInCash
              ? t('depositReview.estimatedAmountLabel')
              : translateBackendString(options.amountCents.label)
          }
          requirement={options.amountCents.required}
          focused={focused}
          onFocus={onFocus}
          onChange={onChange}
          amountError={amountError}
        />
      )}
      {isNearFullWithdrawal(deposit) && (
        <Typography
          variant='labelSmall'
          component='div'
          color='error'
          sx={{ mt: 1 }}
        >
          {t('depositReview.amountExceeds')}
        </Typography>
      )}
    </>
  );
};
